import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { Modal } from '../../../map-editor-shared/ui/Modal'
import {
  exportGraphToJSON,
  downloadJSON,
  validateMapData,
  type FloorData,
} from '../../../map-editor-features/editor/lib/exportUtils'
import { ExportDataSchema } from '../../../map-editor-entities/schema/exportSchema'
import styles from './ExportModal.module.css'
import { log, LogCategory } from '../../../map-editor-shared/lib/logger'
import {
  NVR_TEMPLATE_KINDS,
  type NvrTemplateKind,
} from '../../../map-editor-features/nvr-template/lib/nvrTemplate'

interface LayerInfo {
  layer: string
  elementCount: number
}

// 표준 mapData 키 — 객체 수 집계 시 이 키 외의 배열(레이어 이름 키로 들어온
// 사용자 정의 객체)도 합산하기 위해 사용한다.
const STANDARD_MAP_KEYS = new Set([
  'cctvs',
  'zones',
  'columns',
  'parkingLocations',
  'sensors',
  'chargers',
  'guideBoards',
  'elevators',
  'emergencyBells',
  'arrows',
  'outLines',
  'innerLines',
  'entrances',
  'onePassReaders',
  'occupancyLights',
  'lights',
  'carChargers',
])

interface ExportModalProps {
  isOpen: boolean
  onClose: () => void
  floors: FloorData[]
  lotName: string
  lotCreated: string
}

export function ExportModal({ isOpen, onClose, floors, lotName, lotCreated }: ExportModalProps) {
  const [isExporting, setIsExporting] = useState(false)
  const [isNvrExporting, setIsNvrExporting] = useState(false)
  const [nvrKind, setNvrKind] = useState<NvrTemplateKind>('legacy')
  const [jsonData, setJsonData] = useState<any>(null)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [selectedLayers, setSelectedLayers] = useState<Set<string>>(new Set())
  const [showPreview, setShowPreview] = useState(false)

  // Extract unique layers from all floors
  const layers = useMemo<LayerInfo[]>(() => {
    if (!floors || floors.length === 0) return []
    const layerMap = new Map<string, number>()
    for (const floor of floors) {
      const elements = floor.graph.getElements()
      for (const el of elements) {
        const data = el.get('data')
        const layer = data?.layer
        if (layer) {
          layerMap.set(layer, (layerMap.get(layer) || 0) + 1)
        }
      }
    }
    return Array.from(layerMap.entries())
      .map(([layer, count]) => ({ layer, elementCount: count }))
      .sort((a, b) => a.layer.localeCompare(b.layer))
  }, [floors])

  // Initialize all layers as selected only when modal opens (not on every layers change)
  const prevOpenRef = useRef(false)
  useEffect(() => {
    if (isOpen && !prevOpenRef.current && layers.length > 0) {
      setSelectedLayers(new Set(layers.map((l) => l.layer)))
    }
    if (!isOpen) {
      setShowPreview(false)
    }
    prevOpenRef.current = isOpen
  }, [isOpen, layers])

  const toggleLayer = useCallback((layer: string) => {
    setSelectedLayers((prev) => {
      const next = new Set(prev)
      if (next.has(layer)) {
        next.delete(layer)
      } else {
        next.add(layer)
      }
      return next
    })
  }, [])

  const toggleAll = useCallback(() => {
    setSelectedLayers((prev) => {
      if (prev.size === layers.length) {
        return new Set()
      }
      return new Set(layers.map((l) => l.layer))
    })
  }, [layers])

  // Build layerFilter: if all layers selected or no layers exist, no filter needed
  const layerFilter = useMemo<Set<string> | undefined>(() => {
    if (layers.length === 0) return undefined
    if (selectedLayers.size === layers.length) return undefined
    if (selectedLayers.size === 0) return new Set<string>()
    return selectedLayers
  }, [layers, selectedLayers])

  // Update JSON data when modal opens or selection changes
  // Use selectedLayers directly in dependency to ensure re-generation on every toggle
  useEffect(() => {
    if (isOpen && floors && floors.length > 0) {
      try {
        const data = exportGraphToJSON(
          floors,
          {
            lotName,
            lotCreated,
          },
          layerFilter,
        )

        const validationResult = ExportDataSchema.safeParse(data)
        if (!validationResult.success) {
          const allErrors = validationResult.error.errors
          const maxDisplay = 10
          const displayed = allErrors
            .slice(0, maxDisplay)
            .map((err) => `${err.path.join('.')}: ${err.message}`)
          if (allErrors.length > maxDisplay) {
            displayed.push(`... and ${allErrors.length - maxDisplay} more errors`)
          }
          setValidationError(displayed.join('\n'))
          log.warn(LogCategory.GENERAL, `Export validation failed with ${allErrors.length} errors`)
        } else {
          // 스키마는 통과했지만 현장에서 사고가 나는 값을 잡는다
          // (IP/CCTV 정보와 CCTV-주차구역·비상벨-CCTV 관계).
          const dataIssues = validateMapData(data)
          if (dataIssues.length > 0) {
            setValidationError(dataIssues.join('\n'))
            log.warn(
              LogCategory.GENERAL,
              `Export data validation failed with ${dataIssues.length} issues`,
            )
          } else {
            setValidationError(null)
          }
        }

        setJsonData(data)
      } catch (error) {
        setJsonData(null)
        setValidationError(error instanceof Error ? error.message : 'Unknown error')
      }
    }
  }, [isOpen, floors, lotName, lotCreated, selectedLayers])

  const jsonString = useMemo(() => {
    if (!jsonData) return ''
    return JSON.stringify(jsonData, null, 2)
  }, [jsonData])

  const objectCount = useMemo(() => {
    if (!jsonData?.data?.parkingLotLevels) return 0
    let totalCount = 0
    jsonData.data.parkingLotLevels.forEach((level: any) => {
      const mapData = level.mapData
      if (!mapData) return
      if (mapData.cctvs?.lightCctvs) totalCount += mapData.cctvs.lightCctvs.length
      if (mapData.zones) totalCount += mapData.zones.length
      if (mapData.columns) totalCount += mapData.columns.length
      if (mapData.parkingLocations) totalCount += mapData.parkingLocations.length
      if (mapData.sensors) totalCount += mapData.sensors.length
      if (mapData.chargers) totalCount += mapData.chargers.length
      if (mapData.guideBoards) totalCount += mapData.guideBoards.length
      if (mapData.elevators) totalCount += mapData.elevators.length
      if (mapData.emergencyBells) totalCount += mapData.emergencyBells.length
      if (mapData.arrows) totalCount += mapData.arrows.length
      if (mapData.outLines) totalCount += mapData.outLines.length
      if (mapData.innerLines) totalCount += mapData.innerLines.length
      if (mapData.entrances) totalCount += mapData.entrances.length
      if (mapData.onePassReaders) totalCount += mapData.onePassReaders.length
      if (mapData.occupancyLights) totalCount += mapData.occupancyLights.length
      if (mapData.lights) totalCount += mapData.lights.length
      if (mapData.carChargers) totalCount += mapData.carChargers.length
      // 사용자 정의 레이어 객체는 레이어 이름 키로 들어오므로 표준 키가 아닌 배열을 합산한다.
      Object.entries(mapData).forEach(([key, value]) => {
        if (STANDARD_MAP_KEYS.has(key)) return
        if (Array.isArray(value)) totalCount += value.length
      })
    })
    return totalCount
  }, [jsonData])

  const floorCount = useMemo(() => {
    return jsonData?.data?.parkingLotLevels?.length || 0
  }, [jsonData])

  const linkCount = useMemo(() => {
    return floors.reduce((total, floor) => total + floor.graph.getLinks().length, 0)
  }, [floors])

  const exportBlocked = isExporting || !jsonData || (layers.length > 0 && selectedLayers.size === 0)

  if (!floors || floors.length === 0) return null

  const handleExport = async () => {
    if (exportBlocked) return
    setIsExporting(true)
    try {
      const timestamp = new Date().toISOString().split('T')[0]
      const filename = `map-${lotName}-${timestamp}.json`
      downloadJSON(jsonData, filename)
      await new Promise((resolve) => setTimeout(resolve, 500))
      onClose()
    } catch (error) {
      alert('Export failed. Please try again.')
    } finally {
      setIsExporting(false)
    }
  }

  // 구역별 NVR 원격등록 템플릿을 고른 NVR 형식(기존 .xls / 포커스에이아이 .csv)으로 만들어 zip 으로 다운로드.
  // 생성 로직은 nvr-template feature 에 있다(.xls 는 biff8Xls 자체 작성기 — SheetJS 산출물은 NVR 이 거부).
  const handleNvrExport = async () => {
    setIsNvrExporting(true)
    try {
      const { generateNvrTemplateZip, downloadBlob } = await import(
        '../../../map-editor-features/nvr-template/lib/nvrTemplate'
      )
      const { blob } = await generateNvrTemplateZip(floors.map((f) => f.graph), nvrKind)
      downloadBlob(blob, NVR_TEMPLATE_KINDS[nvrKind].zipName)
    } catch (error) {
      alert(error instanceof Error ? error.message : 'NVR 템플릿 생성에 실패했습니다.')
    } finally {
      setIsNvrExporting(false)
    }
  }

  const handleCopyToClipboard = async () => {
    if (exportBlocked) return
    try {
      await navigator.clipboard.writeText(jsonString)
      alert('JSON copied to clipboard!')
    } catch (error) {
      alert('Failed to copy to clipboard')
    }
  }

  const allSelected = selectedLayers.size === layers.length

  const footer = (
    <div className={styles.footer}>
      <div className={styles.stats}>
        <span>{floorCount} floors</span>
        <span>•</span>
        <span>{objectCount} objects</span>
        <span>•</span>
        <span>{linkCount} connections</span>
      </div>
      <div className={styles.actions}>
        <button
          className={styles.secondaryBtn}
          onClick={handleCopyToClipboard}
          disabled={exportBlocked}
        >
          Copy JSON
        </button>
        <button className={styles.secondaryBtn} onClick={onClose} disabled={isExporting}>
          Cancel
        </button>
        <select
          className={styles.nvrSelect}
          value={nvrKind}
          onChange={(e) => setNvrKind(e.target.value as NvrTemplateKind)}
          disabled={isNvrExporting}
          aria-label="NVR 템플릿 형식"
        >
          {(Object.keys(NVR_TEMPLATE_KINDS) as NvrTemplateKind[]).map((k) => (
            <option key={k} value={k}>
              {NVR_TEMPLATE_KINDS[k].label}
            </option>
          ))}
        </select>
        <button
          className={styles.secondaryBtn}
          onClick={handleNvrExport}
          disabled={isExporting || isNvrExporting}
          title="구역별 NVR 원격등록 템플릿을 고른 형식으로 zip 다운로드"
        >
          {isNvrExporting ? '생성 중...' : 'NVR 템플릿 (.zip)'}
        </button>
        <button className={styles.primaryBtn} onClick={handleExport} disabled={exportBlocked}>
          {isExporting ? 'Exporting...' : 'Export JSON'}
        </button>
      </div>
    </div>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Export Map Data"
      footer={footer}
      maxWidth="1000px"
    >
      <div className={styles.container}>
        <div className={styles.description}>
          Preview the JSON data before exporting. This will download a file containing all map
          objects and their properties.
        </div>

        {layers.length > 0 && (
          <div className={styles.groupSelector}>
            <div className={styles.groupSelectorHeader}>
              <span className={styles.groupSelectorTitle}>Export Layers</span>
              <button className={styles.toggleAllBtn} onClick={toggleAll}>
                {allSelected ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <div className={styles.groupList}>
              {layers.map((info) => (
                <label key={info.layer} className={styles.groupItem}>
                  <input
                    type="checkbox"
                    checked={selectedLayers.has(info.layer)}
                    onChange={() => toggleLayer(info.layer)}
                    className={styles.groupCheckbox}
                  />
                  <span className={styles.groupName}>{info.layer}</span>
                  <span className={styles.groupLayer}>{info.elementCount} objects</span>
                </label>
              ))}
            </div>
            <div className={styles.groupCount}>
              {selectedLayers.size} / {layers.length} layers selected
            </div>
          </div>
        )}

        {validationError && (
          <div
            role="alert"
            style={{
              padding: '12px',
              marginBottom: '16px',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '6px',
              color: '#ef4444',
              fontSize: '14px',
              whiteSpace: 'pre-wrap',
            }}
          >
            <strong>
              {jsonData
                ? '검증 결과 — 오류가 있어도 내보낼 수 있습니다.'
                : '내보내기 데이터 생성 실패'}
            </strong>
            <br />
            {validationError}
          </div>
        )}

        <button
          className={styles.secondaryBtn}
          onClick={() => setShowPreview((prev) => !prev)}
          style={{ alignSelf: 'flex-start' }}
        >
          {showPreview ? 'Hide Preview' : 'Preview'}
        </button>

        {showPreview && (
          <div className={styles.preview}>
            <div className={styles.previewHeader}>
              <span className={styles.previewTitle}>JSON Preview</span>
              <span className={styles.fileSize}>
                {(new Blob([jsonString]).size / 1024).toFixed(2)} KB
              </span>
            </div>
            <pre className={styles.jsonContent}>
              <code>{jsonString}</code>
            </pre>
          </div>
        )}
      </div>
    </Modal>
  )
}
